import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SwipeLeftOutlinedIcon from "@mui/icons-material/SwipeLeftOutlined";

import CashFlowChart from "./CashFlowChart";
import IVAChart from "./IVAChart";
import ProyeccionesChart from "./ProyeccionesChart";
import { getCashFlowCategories, getCategoryRowColors } from "./cashFlowCategories";
import { exportMatrixToExcel } from "./cashFlowMatrixExport";
import { formatAmount } from "./cashFlowUtils";
import { pageContentCardSx, pageMobileCardSx, pageFieldSx } from "../../styles/pageModernStyles";

const MOBILE_TABS = [
  { id: "resumen", label: "Resumen" },
  { id: "dia", label: "Por día" },
  { id: "clientes", label: "Clientes" },
  { id: "proveedores", label: "Proveedores" },
];

function DayDetailView({ theme, days, data, formatValue }) {
  const accent = theme.palette.primary.main;
  const mapByDate = useMemo(() => {
    const map = {};
    (data || []).forEach((r) => {
      map[dayjs(r.fecha).format("YYYY-MM-DD")] = r;
    });
    return map;
  }, [data]);

  const [selectedDay, setSelectedDay] = useState(() =>
    days.length ? days[days.length - 1] : "",
  );

  useEffect(() => {
    if (!days.length) {
      setSelectedDay("");
      return;
    }
    if (!selectedDay || !days.includes(selectedDay)) {
      setSelectedDay(days[days.length - 1]);
    }
  }, [days, selectedDay]);

  const categories = useMemo(
    () => getCashFlowCategories(data, formatValue),
    [data, formatValue],
  );

  const row = selectedDay ? mapByDate[selectedDay] : null;
  const weekday = selectedDay ? dayjs(selectedDay).day() : -1;
  const isWeekend = weekday === 0 || weekday === 6;

  return (
    <Stack spacing={1.25}>
      <Box
        sx={{
          ...pageContentCardSx(theme, accent),
          p: 1.25,
        }}
      >
        <FormControl fullWidth size="small" sx={pageFieldSx(theme)}>
          <InputLabel>Fecha</InputLabel>
          <Select
            value={selectedDay}
            label="Fecha"
            onChange={(e) => setSelectedDay(String(e.target.value))}
          >
            {[...days].reverse().map((d) => (
              <MenuItem key={d} value={d}>
                {dayjs(d).format("DD/MM/YYYY")}
                {dayjs(d).day() === 0 || dayjs(d).day() === 6 ? " · finde" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedDay ? (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={dayjs(selectedDay).format("dddd")}
              sx={{ fontWeight: 700 }}
            />
            {isWeekend ? (
              <Chip size="small" color="error" variant="outlined" label="Fin de semana" />
            ) : null}
            {row?.cashFlowDia != null ? (
              <Chip
                size="small"
                label={`CF día: ${formatValue(row.cashFlowDia) || "0,00"}`}
                sx={{
                  fontWeight: 800,
                  color: Number(row.cashFlowDia) >= 0 ? "success.main" : "error.main",
                }}
              />
            ) : null}
          </Stack>
        ) : null}
      </Box>

      <Stack spacing={0.75}>
        {categories.map((cat) => {
          const value = row?.[cat.key];
          const hasValue = value !== undefined && value !== null && value !== 0;
          if (!hasValue && cat.tone !== "cfDay" && cat.tone !== "cfAcum") return null;

          const colors = getCategoryRowColors(theme, cat.key);
          const isHighlight =
            cat.tone === "cfDay" || cat.tone === "cfAcum" || cat.tone === "balance";

          return (
            <Box
              key={cat.key}
              sx={{
                ...pageMobileCardSx(theme, {
                  active: isHighlight,
                  accentColor:
                    cat.tone === "cfDay"
                      ? theme.palette.success.main
                      : cat.tone === "cfAcum"
                        ? theme.palette.warning.main
                        : accent,
                }),
                bgcolor: colors.rowBg || alpha(theme.palette.background.paper, 0.98),
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}
              >
                {cat.mobileLabel}
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  fontVariantNumeric: "tabular-nums",
                  color:
                    Number(value) < 0 ? theme.palette.error.main : "text.primary",
                }}
              >
                {formatValue(value) || "—"}
              </Typography>
              {cat.key === "facturasVenta" && row?.clientes?.length ? (
                <Stack spacing={0.35} sx={{ mt: 0.75 }}>
                  {row.clientes.map((c, idx) => (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      sx={{ fontSize: "0.78rem" }}
                    >
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {c.nombre}
                      </Typography>
                      <Typography variant="caption" fontWeight={700}>
                        {formatValue(c.importe)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}

function EntityMobileList({
  theme,
  title,
  entityLabel,
  map,
  days,
  formatValue,
  year,
  importeVencido = [],
  fileName,
  chipColor = "primary",
}) {
  const accent = theme.palette.primary.main;
  const [query, setQuery] = useState("");
  const entities = useMemo(() => {
    const q = String(query || "")
      .trim()
      .toLowerCase();
    const all = Object.keys(map || {});
    if (!q) return all;
    return all.filter((name) => name.toLowerCase().includes(q));
  }, [map, query]);

  const entityTotals = useMemo(() => {
    const totals = {};
    entities.forEach((name) => {
      totals[name] = (days || []).reduce(
        (sum, d) => sum + (Number(map[name]?.[d]) || 0),
        0,
      );
    });
    return totals;
  }, [entities, days, map]);

  const sortedEntities = useMemo(
    () =>
      [...entities].sort(
        (a, b) => Math.abs(entityTotals[b]) - Math.abs(entityTotals[a]),
      ),
    [entities, entityTotals],
  );

  const handleExport = () => {
    exportMatrixToExcel({
      entities: sortedEntities,
      days,
      map,
      entityLabel,
      fileName,
    });
  };

  return (
    <Stack spacing={1.25}>
      <Box sx={{ ...pageContentCardSx(theme, accent), p: 1.25 }}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip
            size="small"
            color={chipColor}
            variant="outlined"
            label={`${entities.length} visibles`}
            sx={{ fontWeight: 700 }}
          />
          {importeVencido.map(({ year: vYear, amount }) => (
            <Chip
              key={vYear}
              size="small"
              variant="outlined"
              label={`Venc. ${vYear}: ${formatAmount(amount)}`}
              sx={{ fontWeight: 700 }}
            />
          ))}
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder={`Buscar ${entityLabel.toLowerCase()}…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={pageFieldSx(theme)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleExport}
          disabled={!sortedEntities.length}
          sx={{ mt: 1, fontWeight: 700 }}
        >
          Exportar Excel
        </Button>
      </Box>

      {sortedEntities.length === 0 ? (
        <Typography color="text.secondary" sx={{ px: 0.5, py: 2, textAlign: "center" }}>
          Sin resultados para la búsqueda.
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {sortedEntities.map((name) => {
            const daysWithValues = (days || []).filter((d) => map[name]?.[d]);
            const total = entityTotals[name];
            return (
              <Accordion
                key={name}
                disableGutters
                elevation={0}
                sx={{
                  ...pageMobileCardSx(theme),
                  "&:before": { display: "none" },
                  borderRadius: "10px !important",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ minWidth: 0, pr: 1 }}>
                    <Typography fontWeight={700} sx={{ fontSize: "0.88rem" }}>
                      {name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: total < 0 ? "error.main" : "success.dark",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      Total: {formatValue(total) || "0,00"}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={0.5}>
                    {daysWithValues.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Sin movimientos en el periodo.
                      </Typography>
                    ) : (
                      daysWithValues.map((d) => (
                        <Stack
                          key={d}
                          direction="row"
                          justifyContent="space-between"
                          sx={{
                            py: 0.35,
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.5),
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(d).format("DD/MM")}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontVariantNumeric: "tabular-nums",
                              color:
                                map[name][d] < 0 ? theme.palette.error.main : undefined,
                            }}
                          >
                            {formatValue(map[name][d])}
                          </Typography>
                        </Stack>
                      ))
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

const CashFlowMobile = ({
  theme,
  data,
  days,
  formatValue,
  mapByCliente,
  clienteDays,
  mapByProveedor,
  proveedorDays,
  year,
  importeVencidoClientes,
  importeVencidoProveedores,
}) => {
  const [tab, setTab] = useState("resumen");

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_e, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 1.25,
          minHeight: 40,
          "& .MuiTab-root": {
            minHeight: 40,
            py: 0.75,
            fontWeight: 700,
            fontSize: "0.78rem",
            textTransform: "none",
          },
        }}
      >
        {MOBILE_TABS.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} />
        ))}
      </Tabs>

      {tab === "resumen" ? (
        <Stack spacing={1.25}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 0.5,
              color: "text.secondary",
            }}
          >
            <SwipeLeftOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="caption" fontWeight={600}>
              Gráficos adaptados a móvil. La matriz completa está en escritorio.
            </Typography>
          </Box>
          <CashFlowChart theme={theme} data={data} compact />
          <IVAChart data={data} compact />
          <ProyeccionesChart data={data} compact />
        </Stack>
      ) : null}

      {tab === "dia" ? (
        <DayDetailView theme={theme} days={days} data={data} formatValue={formatValue} />
      ) : null}

      {tab === "clientes" ? (
        <EntityMobileList
          theme={theme}
          title="Facturas por Cliente"
          entityLabel="Cliente"
          map={mapByCliente}
          days={clienteDays}
          formatValue={formatValue}
          year={year}
          importeVencido={importeVencidoClientes}
          fileName={`facturas_clientes_${year}.xlsx`}
          chipColor="primary"
        />
      ) : null}

      {tab === "proveedores" ? (
        <EntityMobileList
          theme={theme}
          title="Facturas por Proveedor"
          entityLabel="Proveedor"
          map={mapByProveedor}
          days={proveedorDays}
          formatValue={formatValue}
          year={year}
          importeVencido={importeVencidoProveedores}
          fileName={`facturas_proveedores_${year}.xlsx`}
          chipColor="secondary"
        />
      ) : null}
    </Box>
  );
};

export default CashFlowMobile;
